import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_APPROVAL, shipmentDocTypes } from 'config/documents'; // Pull this from the document service once documents are retrieved from there
import { underscored } from 'underscore.string';
import { sortBy } from 'lodash';
import Label from 'components/v2/forms/Label';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import Button from 'components/v2/buttons/Button';
import AutocompleteSearch from 'components/v2/AutocompleteSearch';
import CargomaticModal from 'components/v2/modal';
import { upsertAccessorialRequestRule } from 'utils/UserServiceAPIUtils';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import MultiSelectTable from 'components/v2/MultiSelectTable';
import Alert from 'components/ui/Alert';

export default function AccessorialAddEditModal({
  shipperId,
  accessorial,
  onHide,
  editMode,
  addMode,
  onSave,
}) {
  const { t } = useTranslation();

  const sortDocs = (docs) => {
    return sortBy(docs, (x) => x?.name);
  };

  const SHIPMENT_DOCS_MAPPED =
    shipmentDocTypes.map((doc) => {
      return {
        name: t(doc.localizationSlug),
        value: doc.value,
      };
    }).filter((doc) => doc.value !== 'customer_approval');

  const [accessorialName, setAccessorialName] = useState();
  const [customerApprovalRequired, setCustomerApprovalRequired] =
    useState(false);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [allAccessorialsList, setAllAccessorialsList] = useState([]);

  useEffect(() => {
    const fetchAccessorials = async () => {
      try {
        const result = await getAccessorials({
          paginate: false,
        });

        const {
          entity: { items },
        } = result;

        const accessorials = items.map((a) => ({
          name: a?.name,
          value: a?.slug || underscored(a?.name?.toLowerCase()),
        }));
        setAllAccessorialsList(accessorials);
      } catch (e) {
        console.error('error pulling accessorials');
      }
    };

    if (addMode) {
      fetchAccessorials();
    }
  }, [addMode]);

  useEffect(() => {
    if (accessorial && editMode) {
      setAccessorialName(accessorial.accessorial);
      setRequiredDocs(
        sortDocs(
          accessorial.rules?.map((doc) => {
            return { name: t(doc?.value?.toUpperCase()), value: doc?.value };
          }),
        ),
      );
      setCustomerApprovalRequired(accessorial.customer_approval_required);
    }
  }, [accessorial]);

  const handleSave = async () => {
    try {
      const mappedRequiredDocs = requiredDocs.map((doc) => ({
        value: doc.value,
      }));

      const updatedAccessorialRequestRule = {
        customer_approval_required: customerApprovalRequired,
        accessorial: accessorialName,
        ruleSet: mappedRequiredDocs,
      };

      const response = await upsertAccessorialRequestRule(
        shipperId,
        'shipper',
        [updatedAccessorialRequestRule],
      );

      if (!response) {
        throw new Error('No response from API');
      }

      const refreshAccessorial =
        response?.data?.upsertAccessorialRequestRule?.items?.find(
          (acc) => acc.accessorial === accessorialName,
        );
      onSave(refreshAccessorial);
      showToast(t('EDIT_ACCESSORIAL_REQUEST_SUCCESS'));
    } catch (err) {
      console.error('Error during upsertAccessorialRequestRule: ', err.message);
      showToast(t('EDIT_ACCESSORIAL_REQUEST_FAILURE'), null, 'error');
    }
  };

  const handleAccessorialSelect = (accessorial) => {
    setAccessorialName(accessorial.value);
  };

  const handleCustomerApprovalToggle = (isRequired) => {
    if (!isRequired) {
      const docs = requiredDocs.filter(
        (doc) => doc.value !== CUSTOMER_APPROVAL.value,
      );
      setRequiredDocs(docs);
    }
    setCustomerApprovalRequired(isRequired);
  };

  const renderEditAccessorialRequest = () => {
    if (!accessorial && editMode) return null;

    const renderSearchBox = () => {
      if (addMode) {
        return (
          <div className="mb-4">
            <Label labelClassName="block text-sm font-medium text-gray-700 font-semibold">
              {t('ACCESSORIALS')}
            </Label>
            <AutocompleteSearch
              placeholder={t('SEARCH_ACCESSORIAL')}
              items={allAccessorialsList}
              searchKey="name"
              onSelect={handleAccessorialSelect}
              nGramLength={1}
            />
          </div>
        );
      }
    };

    const renderShipperAccessorialInfoLabel = () => {
      if (addMode) {
        return (
          <Alert type="info" className="mt-4">
            {t('ADD_SHIPPER_ACCESSORIAL_INFO_NOTE')}
          </Alert>
        );
      }
    };

    return (
      <>
        {renderSearchBox()}
        {accessorialName && (
          <div className="mb-4">
            <Label labelClassName="block text-sm font-medium text-gray-700 font-semibold">
              {t('ACCESSORIAL')}
            </Label>
            {t(accessorialName?.toUpperCase())}
          </div>
        )}
        <div className="mb-4">
          <Label
            labelClassName="block text-sm font-medium text-gray-700 font-semibold"
            helptext={t('CUSTOMER_APPROVAL_REQUIRED_TOOLTIP')}
          >
            {t('CUSTOMER_APPROVAL_REQUIRED_QUESTION')}
          </Label>
          <ToggleSwitch
            enabled={customerApprovalRequired}
            onChange={handleCustomerApprovalToggle}
          />
        </div>
        <MultiSelectTable
          selectedColumnName={t('REQUIRED_DOCUMENTS')}
          availableColumnName={t('AVAILABLE_DOCUMENTS')}
          columnNamesClassName="block text-sm font-medium text-gray-700 font-semibold text-nowrap"
          selectedItems={requiredDocs}
          allItemsList={SHIPMENT_DOCS_MAPPED}
          onSelect={setRequiredDocs}
        />
        {renderShipperAccessorialInfoLabel()}
      </>
    );
  };

  const handleOnSave = () => {
    handleSave();
    onHide();
    setAccessorialName();
    setAccessorialName();
    setRequiredDocs([]);
    setCustomerApprovalRequired(false);
  };

  const handleOnCancel = () => {
    onHide();
    setAccessorialName();
    setRequiredDocs([]);
    setCustomerApprovalRequired(false);
  };

  const renderEditAccessorialRequestFooter = () => {
    const { t } = useTranslation();
    return (
      <div>
        <Button
          className="m-1"
          color="green"
          type="button"
          onClick={handleOnSave}
          disabled={!accessorialName}
        >
          {t('SAVE')}
        </Button>
        <Button className="m-1" type="button" onClick={handleOnCancel}>
          {t('CANCEL')}
        </Button>
      </div>
    );
  };

  return editMode ? (
    <CargomaticModal
      showCloseButton={false}
      title={t('EDIT_ACCESSORIAL')}
      titleClassNames="text-lg font-medium leading-6 text-gray-700"
      show={!!accessorial && editMode}
      onHide={onHide}
      body={renderEditAccessorialRequest()}
      footer={renderEditAccessorialRequestFooter()}
    />
  ) : (
    <CargomaticModal
      showCloseButton={false}
      title={t('ADD_ALLOWED_ACCESSORIAL')}
      titleClassNames="text-lg font-medium leading-6 text-gray-700"
      show={addMode}
      onHide={onHide}
      body={renderEditAccessorialRequest()}
      footer={renderEditAccessorialRequestFooter()}
    />
  );
}
